import { useEffect, useState } from 'react'
import { gql, useApolloClient } from '@apollo/client'
import { useGlobalForm } from '../Context/GlobalFormStateContext'
import { buildPdfFile } from '../Services/TreckDeductionPdf'
import {
  getTreckRtvsColumnDefs,
  getTreckCbdcColumnDefs,
  getTreckCbColumnDefs,
} from '../Common/Constants'
import { RTVS_DOC_PREFIX, CBDC_DOC_PREFIX } from '../Services/ConstantData'

const SEARCH_ATTACHMENTS = gql`
  query SearchAttachments($receipts: [String]!, $location: String!) {
    searchAttachments(receipts: $receipts, location: $location) {
      fileName
      recordId
      receipt
      location
    }
  }
`
const SEARCH_CHARGEBACK = gql`
  query SearchChargeback(
    $reportName: String!
    $vendorId: String!
    $documentId: String!
  ) {
    searchChargeback(
      reportName: $reportName
      vendorId: $vendorId
      documentId: $documentId
    ) {
      reportName
      result {
        documentNumber
        vendorId
      }
    }
  }
`

const GET_TRECK_INVOICE_FULL_DETAILS = gql`
  query GetTreckInvoiceFullDetails($vendorId: Long!, $documentId: String!) {
    getTreckInvoiceFullDetails(vendorId: $vendorId, documentId: $documentId) {
      header {
        documentNumber
        documentDate
        vendorNumber
        vendorName
        deductionType
        removalType
        locationId
        physicalDisposition
        shipDate
        returnAuthorization
        scac
        proNumber
        deductionCosts
        handlingFeeCosts
        totalDeduction
      }
      details {
        deptClassItem
        style
        itemDescription
        deductionType
        physicalDisposition
        locationId
        removalType
        quantity
        unitCost
        totalProductValue
        handlingFee
        extendedAmount
        returnAuthorization
        proNumber
      }
    }
  }
`

function getColumnDefs(documentNumber: string) {
  const upper = (documentNumber ?? '').toUpperCase()
  if (upper.startsWith(RTVS_DOC_PREFIX)) return getTreckRtvsColumnDefs()
  if (upper.startsWith(CBDC_DOC_PREFIX)) return getTreckCbdcColumnDefs()
  return getTreckCbColumnDefs()
}

function getChargebackType(documentNumber: string): string {
  const upper = (documentNumber ?? '').toUpperCase()
  if (upper.startsWith(RTVS_DOC_PREFIX)) return RTVS_DOC_PREFIX
  if (upper.startsWith(CBDC_DOC_PREFIX)) return CBDC_DOC_PREFIX
  return 'CB'
}
export interface SearchAttachments {
  searchAttachments: [SearchAttachment]
}

export interface SearchAttachment {
  fileName: string
  recordId: string
  receipt: string
  location: string
}

interface SearchAttachmentsVariable {
  receipts: [string]
  location: string
}

interface SearchChargeback {
  searchChargeback: CBResponse
}

interface CBResponse {
  reportName: string
  result: [SearchChargebackResult]
}

interface SearchChargebackResult {
  documentNumber: string
  vendorId: string
}

export interface SearchChargebackVariable {
  reportName: string
  vendorId: string
  documentId: string
}

export default function useAutoExtractedDocuments(
  attchmentSearchVisible: boolean,
): [any] {
  const [state] = useGlobalForm()
  const client = useApolloClient()
  const [deliveryReceipts, setDeliveryReceipts] = useState<any>([])
  const [chargeback, setChargeback] = useState<any>([])
  const [allAttachments, setAllAttachments] = useState<any>([])

  const getDeliveryReceipts = async () => {
    try {
      if (state.location !== undefined) {
        const { data } = await client.query<
          SearchAttachments,
          SearchAttachmentsVariable
        >({
          query: SEARCH_ATTACHMENTS,
          variables: {
            receipts: state.receipts,
            location: state.location,
          },
          fetchPolicy: 'network-only',
        })
        const drAttachments = data?.searchAttachments.map((attch: any) => {
          return {
            ...attch,
            documentSource: 'DR',
            documentType: 'Delivery Receipt',
            documentId: attch?.receipt,
          }
        })
        setDeliveryReceipts(drAttachments)
      }
    } catch (errors: any) {
      console.log('Error while searching for DR', errors?.message)
    }
  }

  const getTreckFallback = async () => {
    try {
      const documentId = state?.documentNumber
      const vendorId = state?.vendorNumber
      const { data } = await client.query({
        query: GET_TRECK_INVOICE_FULL_DETAILS,
        variables: {
          vendorId: Number(vendorId),
          documentId: documentId,
        },
        fetchPolicy: 'network-only',
      })
      const treckData = data?.getTreckInvoiceFullDetails
      if (!treckData) return

      const chargebackType = getChargebackType(documentId ?? '')
      const pdfFile = await buildPdfFile({
        treckHeader: getColumnDefs(documentId ?? ''), // columns filtered by document type
        rowData: treckData.details ?? [],
        headerData: treckData.header,
        chargebackType,
        paymentId: state.paymentId,
        vendorId: vendorId,
        documentId: documentId,
      })

      if (pdfFile) {
        setChargeback([
          {
            fileName: pdfFile.name,
            vendorId: vendorId,
            documentId: documentId,
            documentSource: 'TRECK',
            documentType: 'Chargeback',
            location: state?.location,
            localFile: pdfFile,
          },
        ])
      }
    } catch (errors: any) {
      console.log('Error while fetching Treck invoice data', errors?.message)
    }
  }

  const getChargeback = async () => {
    try {
      const { data } = await client.query<
        SearchChargeback,
        SearchChargebackVariable
      >({
        query: SEARCH_CHARGEBACK,
        variables: {
          reportName: 'credit_debit',
          vendorId: state?.vendorNumber,
          documentId: state?.documentNumber,
        },
        fetchPolicy: 'network-only',
      })
      console.log('cb results', data?.searchChargeback)

      const hasResults = (data?.searchChargeback?.result?.length ?? 0) > 0
      if (hasResults) {
        const chargeback = [
          {
            fileName: `${state?.documentNumber}.pdf`,
            vendorId: state?.vendorNumber,
            documentId: state?.documentNumber,
            documentSource: 'CB',
            documentType: 'Chargeback',
            location: state?.location,
          },
        ]
        setChargeback(chargeback)
      } else {
        await getTreckFallback()
      }
    } catch (errors: any) {
      console.log('Error while searching for CB', errors?.message)
      await getTreckFallback()
    }
  }

  useEffect(() => {
    if (state?.searchAttachmentType?.includes('DR')) {
      getDeliveryReceipts()
    }
    if (state?.searchAttachmentType?.includes('CB')) {
      getChargeback()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attchmentSearchVisible])

  useEffect(() => {
    setAllAttachments([...deliveryReceipts, ...chargeback])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryReceipts, chargeback])

  return allAttachments
}
